/*
  ZEVQILO Atelier — hover-reveal.js
  <hover-reveal>: a list of large links; the hovered (or keyboard-focused) link's photo floats beside
  the pointer, tilting slightly with its speed. Photos are decorative: each link carries its own name.
  Shown only on hover-capable desktop screens (the section's CSS decides); still for visitors who
  prefer reduced motion.
*/
(() => {
  if (customElements.get('hover-reveal')) return;

  /* Focus from the keyboard. A browser that does not know :focus-visible counts every focus. */
  const keyboardFocus = (el) => {
    try {
      return el.matches(':focus-visible');
    } catch (error) {
      return true;
    }
  };

  class HoverReveal extends HTMLElement {
    connectedCallback() {
      this.float = this.querySelector('[data-float]');
      if (!this.float) return;
      this.media = new Map(Array.from(this.querySelectorAll('[data-float-media]')).map((el) => [el.dataset.floatMedia, el]));
      this.x = 0;
      this.y = 0;
      this.cx = 0;
      this.cy = 0;
      this.active = null;
      this.onMove = this.onMove.bind(this);
      this.onOver = this.onOver.bind(this);
      this.onLeave = this.onLeave.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onBlur = this.onBlur.bind(this);
      this.loop = this.loop.bind(this);
      this.addEventListener('pointermove', this.onMove);
      this.addEventListener('pointerover', this.onOver);
      this.addEventListener('pointerleave', this.onLeave);
      this.addEventListener('focusin', this.onFocus);
      this.addEventListener('focusout', this.onBlur);
    }

    disconnectedCallback() {
      this.removeEventListener('pointermove', this.onMove);
      this.removeEventListener('pointerover', this.onOver);
      this.removeEventListener('pointerleave', this.onLeave);
      this.removeEventListener('focusin', this.onFocus);
      this.removeEventListener('focusout', this.onBlur);
      window.cancelAnimationFrame(this.frame);
    }

    get still() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    moveTo(x, y, jump) {
      this.x = x;
      this.y = y;
      if (jump || this.still) {
        this.cx = x;
        this.cy = y;
      }
      if (!this.frame) this.frame = window.requestAnimationFrame(this.loop);
    }

    loop() {
      const dx = this.x - this.cx;
      this.cx += dx * 0.14;
      this.cy += (this.y - this.cy) * 0.14;
      const tilt = Math.max(-9, Math.min(9, dx * 0.06));
      this.float.style.transform = `translate3d(${this.cx.toFixed(1)}px, ${this.cy.toFixed(1)}px, 0) rotate(${tilt.toFixed(2)}deg)`;
      this.frame = Math.abs(dx) + Math.abs(this.y - this.cy) > 0.5 ? window.requestAnimationFrame(this.loop) : 0;
    }

    show(id) {
      const next = this.media.get(id);
      if (!next || next === this.active) return;
      this.media.forEach((el) => el.classList.remove('was-active'));
      if (this.active) {
        this.active.classList.remove('is-active');
        this.active.classList.add('was-active');
      }
      next.classList.add('is-active');
      this.active = next;
      this.float.classList.add('is-visible');
    }

    hide() {
      this.float.classList.remove('is-visible');
    }

    point(event) {
      const rect = this.getBoundingClientRect();
      return [event.clientX - rect.left, event.clientY - rect.top];
    }

    onOver(event) {
      if (event.pointerType !== 'mouse') return;
      const trigger = event.target.closest('[data-float-trigger]');
      if (!trigger) return;
      const wasVisible = this.float.classList.contains('is-visible');
      const [x, y] = this.point(event);
      this.moveTo(x, y, !wasVisible);
      this.show(trigger.dataset.floatTrigger);
    }

    onMove(event) {
      if (event.pointerType !== 'mouse') return;
      if (!event.target.closest('[data-float-trigger]')) {
        this.hide();
        return;
      }
      const [x, y] = this.point(event);
      this.moveTo(x, y, false);
    }

    onLeave() {
      this.hide();
    }

    /* Keyboard: show the photo at the right of the focused row. */
    onFocus(event) {
      const trigger = event.target.closest('[data-float-trigger]');
      if (!trigger || !keyboardFocus(trigger)) return;
      const rect = this.getBoundingClientRect();
      const row = trigger.getBoundingClientRect();
      this.moveTo(rect.width * 0.62, row.top - rect.top + row.height / 2, !this.float.classList.contains('is-visible'));
      this.show(trigger.dataset.floatTrigger);
    }

    onBlur(event) {
      if (!this.contains(event.relatedTarget)) this.hide();
    }
  }

  customElements.define('hover-reveal', HoverReveal);
})();
